import React, { useEffect, useState, useRef } from "react";
import { Container, Row } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import zithasLogo from "../../assets/images/zithas-logo.png";
import secureLocalStorage from "react-secure-storage";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import html2pdf from "html2pdf.js";
import configure from "configure";
import axios from "axios";
import Loader from "components/Common/Loader";

const ViewZtplInvoice = () => {
  function numberToWords(num, currency = "₹") {
    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const currencyNames = {
      $: { main: "Dollar", sub: "Cent" },
      "₹": { main: "Rupee", sub: "Paise" },
      "€": { main: "Euro", sub: "Cent" },
      "£": { main: "Pound", sub: "Pence" },
      CAD: { main: "Canadian Dollar", sub: "Cent" },
      AUD: { main: "Australian Dollar", sub: "Cent" },
    };

    function inWords(n) {
      n = Math.floor(n);
      if (n === 0) return "Zero";
      if (n < 20) return a[n];
      if (n < 100)
        return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
      if (n < 1000)
        return (
          a[Math.floor(n / 100)] +
          " Hundred" +
          (n % 100 ? " " + inWords(n % 100) : "")
        );
      if (n < 1000000)
        return (
          inWords(Math.floor(n / 1000)) +
          " Thousand" +
          (n % 1000 ? " " + inWords(n % 1000) : "")
        );
      if (n < 1000000000)
        return (
          inWords(Math.floor(n / 1000000)) +
          " Million" +
          (n % 1000000 ? " " + inWords(n % 1000000) : "")
        );
      return (
        inWords(Math.floor(n / 1000000000)) +
        " Billion" +
        (n % 1000000000 ? " " + inWords(n % 1000000000) : "")
      );
    }

    const currencyInfo = currencyNames[currency] || {
      main: "Unit",
      sub: "Subunit",
    };

    const whole = Math.floor(num);
    const fraction = Math.round((num - whole) * 100);

    let result =
      inWords(whole) + " " + currencyInfo.main + (whole !== 1 ? "s" : "");

    if (fraction > 0) {
      result +=
        " and " +
        inWords(fraction) +
        " " +
        currencyInfo.sub +
        (fraction !== 1 ? "s" : "");
    }

    result += " Only";

    return result;
  }

  const navigate = useNavigate();
  const params = useParams();
  const [isLoading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState({});
  const [error, setError] = useState(null);
  const token = secureLocalStorage.getItem("auth_token");
  const contentRef = useRef(null);

  document.title = "View Invoice | Zithas CRM";

  useEffect(() => {
    window.scrollTo(0, 0);
    const getInvoiceDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        // Validate invoice type
        const validTypes = ["zithas", "ztpl"];
        if (!params?.type || !validTypes.includes(params.type.toLowerCase())) {
          console.warn(`Invalid or missing invoice type: ${params?.type}. Defaulting to ztplInvoice.`);
          setError("Invalid invoice type in URL. Please check the URL.");
          return;
        }

        // Determine invoice type from URL (e.g., 'ztpl' or 'zithas')
        const invoiceType = params.type.toLowerCase() === "zithas" ? "introducerZithasInvoice" : "introducerInvoice";
        const apiUrl = `${configure.apiUrl}/${invoiceType}/view/${params?.id}`;

        const res = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res?.status === 200) {
          if (!res.data?.data) {
            setError("No invoice data returned from the server.");
            return;
          }
          setInvoiceData(res.data.data || {});
        } else {
          setError(`Failed to fetch invoice data. Status: ${res?.status}`);
        }
      } catch (error) {
        console.error("Error fetching invoice:", error);
        setError(
          `Failed to fetch invoice data: ${error.response?.data?.message || error.message
          }`
        );
      } finally {
        setLoading(false);
      }
    };

    if (params?.id && params?.type) {
      getInvoiceDetail();
    } else {
      setError("Missing invoice ID or type in URL.");
      setLoading(false);
    }
  }, [params?.id, params?.type, token]);

  const reactToPrintFn = useReactToPrint({
    contentRef,
  });

  const hnsOptions = [
    { value: "998313", label: "998313 - Support & consulting service" },
    { value: "998314", label: "998314 - Design & Development Service" },
    { value: "998315", label: "998315 - Hosting Services" },
    { value: "8543", label: "8543 - Hardware" },
  ];

  const onDownloadInvoice = () => {
    setLoading(true);
    const content = contentRef.current;

    const opt = {
      filename: `Invoice-${invoiceData?.invoice_number || "unknown"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { dpi: 192, letterRendering: true, scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    html2pdf()
      .from(content)
      .set(opt)
      .save()
      .then(() => setLoading(false))
      .catch((err) => {
        console.error("PDF generation error:", err);
        setLoading(false);
        setError("Failed to generate PDF.");
      });
  };

  return (
    <React.Fragment>
      {isLoading && <Loader />}
      <div className="page-content mb-4">
        <Container fluid>
          {error && <div className="alert alert-danger">{error}</div>}
          <Row>
            <div className="col-md-9 ps-0 d-flex justify-content-center">
              <div id="zithasInvoicePrint" className="col-md-11 p-3 document">
                <div
                  className="invoice-view p-4 pb-5 bg-white"
                  ref={contentRef}
                >
                  <div className="row justify-content-between">
                    <div className="col-4">
                      <img
                        src={zithasLogo}
                        className="img-fluid mt-2"
                        alt="Zithas Logo"
                      />
                    </div>
                    <div className="col-4 text-end">
                      <span>
                        Zithas Technologies Pvt. Ltd. FF-16 Kalpvrux Complex,
                        Opp. GEB Substation, Gotri Road Vadodara Gujarat 390021
                        India <br /> GSTIN 24AABCZ2004K1ZL
                      </span>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-4 pe-0">
                      <div className="invoice-line"></div>
                    </div>
                    <div className="col-4">
                      <p className="fs-4 py-3 text-center bg-white mb-0 fw-bold">
                        TAX INVOICE
                      </p>
                    </div>
                    <div className="col-4 ps-0">
                      <div className="invoice-line"></div>
                    </div>
                  </div>

                  <div className="row justify-content-between">
                    <div className="col-4">
                      <p className="mb-1">Bill To</p>
                      <p className="mb-1">
                        {invoiceData?.customer_name || invoiceData?.c_company || "N/A"}
                      </p>
                      <p className="mb-1">
                        {invoiceData?.c_address || "N/A"},{" "}
                        {invoiceData?.city_name || "N/A"},
                      </p>
                      <p className="mb-1">
                        {invoiceData?.state_name || "N/A"},{" "}
                        {invoiceData?.country_name || "N/A"} -{" "}
                        {invoiceData?.c_zipcode || "N/A"}
                      </p>
                      <p className="mb-1">
                        GST: {invoiceData?.c_vatnumber || "N/A"}
                      </p>
                    </div>
                    <div className="col-5 ps-0">
                      <table className="table">
                        <tbody>
                          <tr>
                            <th className="border border-secondary py-1">
                              Invoice#
                            </th>
                            <td className="border border-secondary py-1">
                              {params?.type == "zithas" ? invoiceData?.invoice_number : invoiceData?.tax_inv_no || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <th className="border border-secondary py-1">
                              Invoice Date
                            </th>
                            <td className="border border-secondary py-1">
                              {invoiceData?.invoice_date
                                ? moment(invoiceData.invoice_date).format(
                                  "DD-MM-YYYY"
                                )
                                : "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <th className="border border-secondary py-1">
                              Terms
                            </th>
                            <td className="border border-secondary py-1">
                              {invoiceData?.terms || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <th className="border border-secondary py-1">
                              Due Date
                            </th>
                            <td className="border border-secondary py-1">
                              {invoiceData?.due_date
                                ? moment(invoiceData.due_date).format(
                                  "DD-MM-YYYY"
                                )
                                : "N/A"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="mt-4">
                    <table className="table">
                      <thead>
                        <tr>
                          <th className="border border-secondary">#</th>
                          <th className="border border-secondary">
                            Item & Description
                          </th>
                          <th className="border border-secondary">HSN/SAC</th>
                          <th className="border border-secondary">Qty</th>
                          <th className="border border-secondary">Rate</th>
                          <th className="border border-secondary">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceData?.items?.length > 0 ? (
                          invoiceData.items.map((obj, i) => (
                            <tr key={i}>
                              <td className="border border-secondary">
                                {i + 1}
                              </td>
                              <td className="border border-secondary">
                                <div className="fw-bold">
                                  {obj?.project_name || ""}
                                </div>
                                <div className="fw-bold">
                                  {obj?.service_name || ""}
                                </div>
                                <span>{obj?.service_detail || ""}</span>
                              </td>
                              <td className="border border-secondary">
                                {hnsOptions.find(
                                  (opt) => opt.value === obj?.hsn
                                )?.label || "N/A"}
                              </td>
                              <td className="border border-secondary">
                                {obj?.quantity || 0}
                              </td>
                              <td className="border border-secondary">
                                {Number(obj?.rate || 0).toFixed(2)}
                              </td>
                              <td className="border border-secondary">
                                {invoiceData?.currency_symbol || "₹"}{" "}
                                {(
                                  Number(obj?.rate || 0) *
                                  Number(obj?.quantity || 0)
                                ).toFixed(2)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center">
                              No items available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          invoiceData?.customer_note ||
                          "<p>No notes available</p>",
                      }}
                    />
                  </div>

                  <div className="row justify-content-end">
                    <div className="col-5">
                      <div className="d-flex py-1 mt-3 justify-content-between">
                        <span>Sub Total</span>
                        <span>
                          {invoiceData?.currency_symbol || "₹"}{" "}
                          {Number(invoiceData?.sub_total || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="d-flex py-1 justify-content-between">
                        <span>Discount</span>
                        <span>
                          {Number(invoiceData?.discount_amount || 0).toFixed(2)}
                        </span>
                      </div>
                      {params.type != "zithas" ? (
                        <>
                          {invoiceData?.c_state === "12" ? (
                            <>
                              <div className="d-flex py-1 justify-content-between">
                                <span>CGST (9%)</span>
                                <span>
                                  {invoiceData?.currency_symbol || "₹"}{" "}
                                  {Number(invoiceData?.cgst || 0).toFixed(2)}
                                </span>
                              </div>
                              <div className="d-flex py-1 pb-3 justify-content-between">
                                <span>SGST (9%)</span>
                                <span>
                                  {invoiceData?.currency_symbol || "₹"}{" "}
                                  {Number(invoiceData?.sgst || 0).toFixed(2)}
                                </span>
                              </div>
                            </>
                          ) : invoiceData?.c_country === "1" &&
                            invoiceData?.c_state !== "12" ? (
                            <div className="d-flex pt-1 pb-3 justify-content-between">
                              <span>IGST (18%)</span>
                              <span>
                                {invoiceData?.currency_symbol || "₹"}{" "}
                                {Number(invoiceData?.igst || 0).toFixed(2)}
                              </span>
                            </div>
                          ) : null}
                        </>
                      ) : null}
                      <div className="d-flex pt-2 border-top border-secondary pb-2 justify-content-between">
                        <strong>
                          Total ({invoiceData?.currency_symbol || "₹"})
                        </strong>
                        <span>
                          {Number(invoiceData?.total || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 row pb-5">
                    <p>
                      Total in Words:{" "}
                      <strong>
                        {numberToWords(
                          Number(invoiceData?.total || 0),
                          invoiceData?.currency_symbol || "₹"
                        )}
                      </strong>
                    </p>

                    <h4 className="fw-bold">Bank Details</h4>
                    <div className="col-5">
                      <table className="table">
                        <tr>
                          <th>Bank Name:</th>
                          <td>Indusind Bank Ltd.</td>
                        </tr>
                        <tr>
                          <th>Branch Name:</th>
                          <td>Jetulpur Rd. Branch</td>
                        </tr>
                        <tr>
                          <th>Account Name:</th>
                          <td>Zithas Technologies Pvt. Ltd.</td>
                        </tr>
                        <tr>
                          <th>Account No.:</th>
                          <td>258141122619</td>
                        </tr>
                        <tr>
                          <th>IFSC Code:</th>
                          <td>INDB0000017</td>
                        </tr>
                      </table>
                    </div>

                    <h4 className="fw-bold">Terms & Conditions</h4>
                    <p className="mb-0">
                      1. If payment made by crossed cheque in favour of Zithas
                      Technologies Pvt. Ltd.
                    </p>
                    <p className="mb-0">
                      2. If NEFT / RTGS / Online Transfer should be made to
                      given bank details.
                    </p>
                    <p className="mb-0">
                      3. Late payment charges will be applicable.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-3 pe-0">
              <div>
                <div className="col-12">
                  <button
                    className="btn btn-sm btn-success border-radius me-1"
                    onClick={reactToPrintFn}
                  >
                    <i className="fas fa-print me-1"></i> Print
                  </button>
                  <button
                    className="btn btn-sm btn-secondary border-radius me-1"
                    onClick={onDownloadInvoice}
                  >
                    <i className="fas fa-file-download me-1"></i> Download
                  </button>
                  <button
                    onClick={() => navigate(-1)}
                    className="btn btn-sm btn-red border-radius"
                  >
                    <i className="fas fa-times fs-6 me-1"></i> Cancel
                  </button>
                </div>

                <div className="bg-white p-3 mt-4 shadow">
                  <h5 className="mb-4 text-dark">
                    Invoice Value: {invoiceData?.currency_symbol || "₹"}{" "}
                    {Number(invoiceData?.total || 0).toFixed(2)}
                  </h5>
                  <h6 className="mb-3">
                    Invoice Number: {params?.type == "zithas" ? invoiceData?.invoice_number : invoiceData?.tax_inv_no || "N/A"}
                  </h6>
                  <h6 className="mb-3">
                    Start Date:{" "}
                    {invoiceData?.start_date
                      ? moment(invoiceData.start_date).format("DD-MM-YYYY")
                      : "N/A"}
                  </h6>
                  <h6 className="mb-3">
                    Due Date:{" "}
                    {invoiceData?.due_date
                      ? moment(invoiceData.due_date).format("DD-MM-YYYY")
                      : "N/A"}
                  </h6>
                </div>
              </div>
            </div>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ViewZtplInvoice;